import React, { useState, useEffect } from 'react';
import SelectField from '../components/SelectField';
import InfoCard from '../components/InfoCard';
import { InlineSpinner } from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { qa, validation } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';

const Projects = ({ setAlert }) => {
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [campName, setCampName] = useState('');
  const [campDescription, setCampDescription] = useState('');
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '', success: false });

  useEffect(() => {
    const loadScorecards = async () => {
      try {
        const res = await qa();
        // Live GET /templates shape: { templates: [{ templateName, language }], count };
        // fall back defensively to the legacy raw-DynamoDB shape.
        const names = Array.isArray(res?.templates)
          ? [...new Set(res.templates.map(t => t?.templateName).filter(Boolean))]
          : [...new Set((res.Items || res.items || []).map(i => i.scorecard?.S || i.template?.S).filter(Boolean))];
        setScorecards(names.map(n => ({ label: n, value: n })));
      } catch (err) {
        console.error('Failed to load scorecards:', err);
      }
    };
    loadScorecards();
  }, []);

  const steps = [
    {
      title: 'Craft your Campaign',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign Name</label>
            <input
              type="text"
              value={campName}
              onChange={(e) => setCampName(e.target.value)}
              className="input-field"
              placeholder="Enter campaign name..."
            />
            <p className="text-sm text-gray-500 mt-1">Choose a descriptive name for your quality assurance campaign</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign Description</label>
            <textarea
              rows={4}
              className="input-field"
              placeholder="Describe the purpose and scope of this campaign..."
              value={campDescription}
              onChange={(e) => setCampDescription(e.target.value)}
            />
          </div>
        </div>
      )
    },
    {
      title: 'Select Scorecard',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scorecard</label>
            <SelectField
              value={selectedScorecard}
              onChange={(e) => setSelectedScorecard(e.target.value)}
              className="select-field"
            >
              <option value="">Select scorecard...</option>
              {scorecards.map(sc => (
                <option key={sc.value} value={sc.value}>{sc.label}</option>
              ))}
            </SelectField>
          </div>
        </div>
      )
    },
    {
      title: 'Review and Launch',
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Campaign Details</h5>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">Name:</dt>
                    <dd className="text-gray-900">{campName || 'Not specified'}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Scorecard</h5>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">Selected:</dt>
                    <dd className="text-gray-900">{selectedScorecard || 'None'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const canProceed = () => {
    switch (activeStepIndex) {
      case 0:
        return campName.trim().length > 0;
      case 1:
        return selectedScorecard.trim().length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // fName: '' keeps the upload handler's question-row loop from throwing a
      // TypeError (it concatenates field.get('fName'), which is None when the
      // key is absent). An empty string concatenates fine, and the resulting
      // question row is rejected by DynamoDB (empty sort key) inside the
      // handler's own try/except — so the campaign/template rows are written
      // and the request returns 200 without polluting the scorecard.
      await validation([{ campName, tempName: selectedScorecard, description: campDescription, fName: '' }]);
      setAlert({ variant: 'success', heading: 'Campaign Created', text: 'Your campaign has been created successfully!' });
      setConfirmation({ open: true, title: 'Campaign Created', message: 'Your campaign has been created successfully!', success: true });
    } catch (error) {
      console.error('Error creating campaign:', error);
      const msg = error.message === 'Network error'
        ? 'Network error while contacting the API. Please check your connection or try again later.'
        : 'Failed to create campaign. Please try again.';
      setAlert({ variant: 'error', heading: 'Error', text: msg });
      setConfirmation({ open: true, title: 'Error', message: msg, success: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
        <p className="text-gray-600 mt-1">Set up a new quality assurance campaign</p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <nav aria-label="Progress" className="relative w-full">
          <ol className="relative flex justify-between items-center">
            {steps.map((step, index) => (
              <li key={index} className="relative flex-1 flex flex-col items-center text-center">
                <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${index <= activeStepIndex ? 'bg-cyan-600 text-white' : 'bg-gray-300 text-gray-500'}`}> 
                  {index < activeStepIndex ? (
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                {index !== steps.length - 1 && (
                  <div className={`absolute left-1/2 top-1/2 w-full h-0.5 -z-10 transform -translate-y-1/2 ${index < activeStepIndex ? 'bg-cyan-600' : 'bg-gray-300'}`} />
                )}
                <div className={`mt-2 text-sm font-medium ${index <= activeStepIndex ? 'text-gray-900' : 'text-gray-500'}`}>{step.title}</div>
              </li>
            ))}
          </ol>
        </nav>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">{steps[activeStepIndex].title}</h2>
        {steps[activeStepIndex].content}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 mt-6 border-t border-gray-200">
          <button onClick={handlePrevious} disabled={activeStepIndex === 0} className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="text-sm text-gray-500">Step {activeStepIndex + 1} of {steps.length}</div>
          <button onClick={handleNext} disabled={!canProceed() || isSubmitting} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {isSubmitting ? (
              <>
                <InlineSpinner light className="mr-2" />
                Creating...
              </>
            ) : activeStepIndex === steps.length - 1 ? (
              <>
                Create Campaign
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            ) : (
              <>
                Next
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
      <InfoCard
        id="campaign-setup"
        title="Campaign Setup Tips"
        items={[
          'Choose descriptive names that clearly identify the campaign purpose',
          'Select an existing scorecard that matches your campaign goals',
        ]}
      />
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => {
          setConfirmation({ ...confirmation, open: false });
          if (confirmation.success) {
            navigate('/campaigns');
          }
        }}
      />
    </div>
  );
};

export default Projects;

